/**
 * <upsell-item> powers a single row in the "Produits complémentaires" upsell
 * module (blocks/upsell-produits.liquid). It keeps its own select dropdowns,
 * hidden variant id input, price display, and selection checkbox in sync
 * client-side, without touching the theme's native <variant-picker>
 * component (that component is scoped to the current page's own product and
 * is not safe to reuse for a secondary product rendered elsewhere on the
 * page).
 *
 * Checking an upsell item does not add it to the cart by itself. The module
 * is placed before the product's main "Ajouter au panier" button on
 * purpose: checked items are collected and added to the cart together with
 * the main product, right after it, when that button is clicked (see the
 * page-level listener below).
 */
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class UpsellItem extends HTMLElement {
  connectedCallback() {
    const variantsScript = this.querySelector('[data-upsell-variants]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));
    this.hiddenInput = this.querySelector('[data-upsell-variant-id]');
    this.priceEl = this.querySelector('[data-upsell-price]');
    this.checkbox = this.querySelector('[data-upsell-checkbox]');
    this.moneyFormat = this.dataset.moneyFormat;
    this.currency = this.dataset.currency;

    this.selects.forEach((select) => {
      select.addEventListener('change', () => this.updateVariant());
    });

    this.checkbox?.addEventListener('change', () => this.#updateSelectedState());
  }

  updateVariant() {
    if (this.selects.length === 0) return;

    const selected = this.selects
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((select) => select.value);

    const match = this.variants.find((variant) =>
      selected.every((value, index) => variant.options[index] === value)
    );

    const available = Boolean(match?.available);

    if (this.hiddenInput) {
      this.hiddenInput.value = match ? match.id : '';
      this.hiddenInput.disabled = !available;
    }

    if (this.priceEl && match && this.moneyFormat) {
      const priceNode = this.priceEl.firstChild;
      const formatted = formatMoney(match.price, this.moneyFormat, this.currency);
      if (priceNode) {
        priceNode.textContent = formatted;
      } else {
        this.priceEl.textContent = formatted;
      }
    }

    if (this.checkbox) {
      this.checkbox.disabled = !available;
      if (!available) this.checkbox.checked = false;
      this.#updateSelectedState();
    }
  }

  #updateSelectedState() {
    this.classList.toggle('upsell-item--selected', Boolean(this.checkbox?.checked));
  }

  /**
   * @returns {{id: number, quantity: number} | null}
   */
  getSelectedLine() {
    if (!this.checkbox || !this.checkbox.checked || this.checkbox.disabled) return null;
    if (!this.hiddenInput?.value || this.hiddenInput.disabled) return null;

    return { id: Number(this.hiddenInput.value), quantity: 1 };
  }

  /** Clears the selection, e.g. once the item has just been added to the cart. */
  clearSelection() {
    if (!this.checkbox) return;
    this.checkbox.checked = false;
    this.#updateSelectedState();
  }
}

if (!customElements.get('upsell-item')) {
  customElements.define('upsell-item', UpsellItem);
}

/**
 * @returns {Array<{id: number, quantity: number}>}
 */
function getCheckedUpsellLines() {
  const lines = [];
  document.querySelectorAll('upsell-item').forEach((item) => {
    if (!(item instanceof UpsellItem)) return;
    const line = item.getSelectedLine();
    if (line) lines.push(line);
  });
  return lines;
}

/**
 * Adds the checked upsell items to the cart as a follow-up batch call, then
 * dispatches the same CartLinesUpdateEvent the theme's own product form
 * uses so the cart drawer, cart count, and cart page all refresh normally.
 * @param {Array<{id: number, quantity: number}>} items
 */
function addUpsellItemsToCart(items) {
  if (!items.length) return;

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.push(component.dataset.sectionId);
    }
  });

  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'upsell',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
      sections: sectionIds.join(','),
    }),
  })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        deferred.reject(new Error(response.message || 'Add to cart failed'));
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'upsell-products',
          itemCount: cart.item_count,
          sections: response.sections,
          didError: false,
        },
      });

      // Reset the checked items so a second add-to-cart click doesn't add
      // them again.
      document.querySelectorAll('upsell-item').forEach((item) => {
        if (item instanceof UpsellItem) item.clearSelection();
      });
    })
    .catch((error) => {
      console.error(error);
      deferred.reject(error);
    });
}

// Listen for the main product form's add-to-cart action (context "product",
// action "add") and, once it has gone through, add any checked upsell
// items right after it. Using "upsell" as the context on our own dispatch
// keeps this listener from reacting to its own follow-up call.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.context !== 'product' || event.action !== 'add') return;

  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;

      const items = getCheckedUpsellLines();
      if (items.length === 0) return;

      addUpsellItemsToCart(items);
    })
    .catch(() => {});
});
